import { FC } from 'react';
import { Link } from '@chakra-ui/react';

import {
  InvoiceSendingMethod,
  InvoiceStatus,
  InvoiceType,
  InvoicingPdf,
} from '__generated__/GQL';
import getErrorMessageIfPossible from 'common/hooks/useErrorHandledMutation/getErrorMessageIfPossible';
import { SupportedLanguages } from 'common/locale';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import FiscalIdModal from 'features/Work/components/FiscalIdModal';
import useLastInvoiceFiscalId from 'features/Work/hooks/useLastInvoiceFiscalId';
import { useInvoiceModals } from 'features/Work/pages/Invoices/hooks/useInvoiceModals';
import { downloadPdf } from 'features/Work/utils';

import { INVOICE_NUMBER_ARTICLE } from '../../../../../../constants';
import useMarkInvoiceAsSent from '../../../mutations/useMarkInvoiceAsSent';
import locales from './locales';

export interface DownloadModalProps {
  invoiceId: string;
  status: InvoiceStatus;
  lang: SupportedLanguages;
  pdf: InvoicingPdf | null;
}

const DownloadModal: FC<DownloadModalProps> = ({
  invoiceId,
  lang,
  pdf,
  status,
}) => {
  const { showToast } = useToast();
  const { hideModal } = useInvoiceModals();

  const { lastInvoiceFiscalId, loading } = useLastInvoiceFiscalId();
  const { onMarkInvoiceAsSent } = useMarkInvoiceAsSent();

  const onSubmit = async (fiscalId: string) => {
    try {
      logEvent({
        name: 'Download Invoice',
        properties: {
          from: 'InvoiceDetailsPage',
          invoiceId,
          lang,
        },
      });

      let invoicingPdf = pdf;
      if (status === InvoiceStatus.Pending) {
        const { data } = await onMarkInvoiceAsSent({
          input: {
            fiscalId,
            invoiceId,
            method: InvoiceSendingMethod.Downloaded,
          },
        });

        invoicingPdf = data?.markInvoiceAsSent?.invoice.pdf ?? pdf;
        showToast({
          message: locales.formatString(
            locales.successMessage,
            data?.markInvoiceAsSent?.invoice?.fiscalId ?? '',
          ) as string,
          type: 'success',
        });
      }

      downloadPdf({ lang, pdf: invoicingPdf });

      hideModal();
    } catch (error) {
      showToast({
        message: getErrorMessageIfPossible({ error }) || locales.errorMessage,
        type: 'error',
      });
    }
  };

  return (
    <FiscalIdModal onClose={hideModal}>
      <FiscalIdModal.Body>
        <FiscalIdModal.Body.Title>{locales.title}</FiscalIdModal.Body.Title>

        <FiscalIdModal.Body.Description>
          {locales.description.start}
          <Link
            color="blue.600"
            fontWeight="weight-400"
            href={INVOICE_NUMBER_ARTICLE}
            target="_blank"
          >
            {locales.description.link}
          </Link>
          {locales.description.end}
        </FiscalIdModal.Body.Description>

        <FiscalIdModal.Body.Input
          fiscalId={lastInvoiceFiscalId?.next ?? ''}
          lastFiscalId={lastInvoiceFiscalId?.last ?? undefined}
          loading={loading}
          type={InvoiceType.Invoice}
        />
      </FiscalIdModal.Body>

      <FiscalIdModal.Footer>
        <FiscalIdModal.Footer.CancelButton onClick={hideModal}>
          {locales.cancel}
        </FiscalIdModal.Footer.CancelButton>

        <FiscalIdModal.Footer.SubmitButton onClick={onSubmit}>
          {locales.submit}
        </FiscalIdModal.Footer.SubmitButton>
      </FiscalIdModal.Footer>
    </FiscalIdModal>
  );
};

export default DownloadModal;
